import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  model
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  AbstractControl
} from '@angular/forms';
import { DragulaService } from 'ng2-dragula';

@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html'
})
export class DynamicFormComponent implements OnChanges {
  @Input() schema: any[] = [];        // Form field definitions

  @Input() data: any = {};                     // Current form values
  @Output() dataChange = new EventEmitter<any>();
  
  
  expanded: { [key: string]: { [index: number]: boolean } } = {};


  @Input() useQuill = false;          // Toggle for ngx-quill editor
  @Input() hideButtons = false;       // Toggle for save/reset buttons
  @Output() formSubmit = new EventEmitter<any>();

  form: FormGroup = this.fb.group({}); // Main form group

  constructor(private fb: FormBuilder, private dragula: DragulaService) {
     // LISTS
  this.dragula.dropModel().subscribe(({ name, el, target, source, item, sourceModel, targetModel }) => {
    // name === LIST_key or REPEATABLE_key
    if (!name.startsWith("LIST_") && !name.startsWith("REPEATABLE_")) return;

    const key = name.replace("LIST_", "").replace("REPEATABLE_", "");
    const arr = this.getFormArray(key);
    
    if (!arr) return;

    // Clear form controls and rebuild in new order
    arr.clear();
    targetModel.forEach((ctrl: any) => arr.push(ctrl));

    console.log("UPDATED ORDER:", arr.value);
  });
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log('[DynamicForm] ngOnChanges detected:', changes);
    if (changes['schema'] || changes['data']) {
      try {
        this.buildForm();
        console.log('[DynamicForm] Data NG CHANGE:', this.data)
        console.log('[DynamicForm] Form rebuilt after changes:', this.form.value);
      } catch (err) {
        console.error('[DynamicForm] Error building form:', err);
      }
    }
  }

  /** Build the form from schema + data */
  private buildForm() {
    if (!Array.isArray(this.schema)) {
      console.warn('[DynamicForm] Schema must be an array, received:', this.schema);
      this.schema = [];
    }

    this.form = this.fb.group({});
    console.log('[DynamicForm] Initializing empty form group');

    for (const field of this.schema) {
      if (!field?.key) {
        console.warn('[DynamicForm] Skipping field without key:', field);
        continue;
      }

      const path = field.key;
      const fieldType = field.type || 'text';
      const existingValue = this.getValueByPath(this.data, path);
      console.log('[DynamicForm] Existing value for', {path, existingValue, data: this.data});
      const value = existingValue ?? field.default ?? (fieldType === 'boolean' ? false : '');

      console.log(`[DynamicForm] Building control for ${path}`, {
        type: fieldType,
        existingValue,
        defaultValue: field.default,
        finalValue: value
      });

      try {
        switch (fieldType) {
          case 'repeatable': {
            const items = (Array.isArray(existingValue) ? existingValue : []) as any[];
            const arr = this.fb.array(items.map(item => this.buildRepeatableGroup(field.fields || [], item)));
            this.addControlToForm(this.form, path, arr);
            break;
          }

          case 'list': {
            const listValues = (Array.isArray(existingValue) ? existingValue : []) as any[];
            const arr = this.fb.array(listValues.map(v => this.fb.control(v)));
            this.addControlToForm(this.form, path, arr);
            break;
          }

          default: {
            const validators = [];
            if (field.required) validators.push(Validators.required);
            const control = this.fb.control(value, validators);
            this.addControlToForm(this.form, path, control);
            break;
          }
        }
      } catch (err) {
        console.error(`[DynamicForm] Failed to create control for ${path}`, err);
      }
    }
    

    console.log('[DynamicForm] Final form structure:', this.form.value);
  }

  /** Build a group for repeatable fields */
  private buildRepeatableGroup(fields: any[], dataItem: any) {
    console.log('[DynamicForm] Building repeatable group:', { fields, dataItem });
    const g = this.fb.group({});
    for (const f of fields || []) {
      if (!f?.key) {
        console.warn('[DynamicForm] Repeatable field missing key:', f);
        continue;
      }
      const val = (dataItem && dataItem[f.key] !== undefined)
        ? dataItem[f.key]
        : (f.default ?? '');
      g.addControl(f.key, this.fb.control(val, f.required ? Validators.required : []));
      console.log(`[DynamicForm] Added control to repeatable group: ${f.key} =`, val);
    }
    return g;
  }

  /** Add control to form while respecting nested paths */
  private addControlToForm(root: FormGroup, path: string, control: AbstractControl) {
    console.log('[DynamicForm] Adding control to path:', path);
    const parts = path.split('.');
    let cur: FormGroup = root;

    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      let next = cur.get(part) as FormGroup;
      if (!(next instanceof FormGroup)) {
        next = this.fb.group({});
        cur.addControl(part, next);
        console.log(`[DynamicForm] Created nested group at path: ${parts.slice(0, i + 1).join('.')}`);
      }
      cur = next;
    }

    const last = parts[parts.length - 1];
    if (cur.get(last)) {
      console.log(`[DynamicForm] Replacing existing control at ${path}`);
      cur.removeControl(last);
    }
    cur.addControl(last, control);
  }

  /** Safely resolve value from nested path */
  private getValueByPath(obj: any, path: string): any {
    if (!obj || !path) return undefined;
    try {
      return path.split('.').reduce((acc, p) => acc && acc[p] !== undefined ? acc[p] : undefined, obj);
    } catch (err) {
      console.warn('[DynamicForm] Failed to resolve path:', path, err);
      return undefined;
    }
  }

  private setValueByPath(obj: any, path: string, value: any) {
    if (!obj || !path) return;
    try {
      path.split('.').reduce((acc, p) => acc && acc[p] !== undefined ? acc[p] : undefined, obj);
    } catch (err) {
      console.warn('[DynamicForm] Failed to resolve path:', path, err);
    }
  }
  // ---------------- Template Helpers ----------------

  getFormArray(path: string): FormArray {
    const ctrl = this.form.get(path);
    if (!(ctrl instanceof FormArray)) {
      console.warn(`[DynamicForm] Expected FormArray at ${path}, got`, ctrl);
      return this.fb.array([]);
    }
    return ctrl;
  }

  onAddRepeatable(field: any) {
    console.log('[DynamicForm] Adding new repeatable item for field:', field.key);
    const arr = this.getFormArray(field.key);
    arr.push(this.buildRepeatableGroup(field.fields || [], {}));
  }

  onRemoveRepeatable(field: any, index: number) {
    console.log(`[DynamicForm] Removing repeatable item at index ${index} for field:`, field.key);
    const arr = this.getFormArray(field.key);
    if (index >= 0 && index < arr.length) arr.removeAt(index);
  }

  toggleExpand(fieldKey: string, index: number) {
    if (!this.expanded[fieldKey]) this.expanded[fieldKey] = {};
    this.expanded[fieldKey][index] = !this.expanded[fieldKey][index];
  }

  addListItem(path: string) {
    console.log('[DynamicForm] Adding new list item at path:', path);
    this.getFormArray(path).push(this.fb.control(''));
  }

  removeListItem(path: string, i: number) {
    console.log(`[DynamicForm] Removing list item at index ${i} for path:`, path);
    const arr = this.getFormArray(path);
    if (i >= 0 && i < arr.length) arr.removeAt(i);
  }

  submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      console.warn('[DynamicForm] Form is invalid, value:', this.form.value);
      return;
    }
  
    console.log('[DynamicForm] Submitting form:', this.form.value);
  
    // Create a deep copy of the original data so we don’t mutate @Input directly
    const updatedData = JSON.parse(JSON.stringify(this.data || {}));
  
    // Merge form values into the full data object
    Object.assign(updatedData, this.form.value);
  
    // Emit both events
    this.formSubmit.emit(updatedData);
    this.dataChange.emit(updatedData);
  
    console.log('[DynamicForm] Updated data emitted:', updatedData);
  }
  
}
